import { API_ENDPOINTS } from '../constants/endpoints.ts';

export interface FreshdeskAgent {
	id: string;
	name: string | null;
	email: string | null;
	role: string | null;
}

export interface FreshdeskVerificationResult {
	credentials_accepted: true;
	capabilities_proven: false;
	subdomain: string;
	agent: FreshdeskAgent;
	webhook_token: string;
	verification_receipt: string;
	verification_expires_at: string;
}

export interface FreshdeskConnectPayload {
	subdomain: string;
	api_key: string;
	webhook_token: string;
	verification_receipt: string;
}

export interface FreshdeskRotationResult {
	webhook_token: string;
	expires_at: string;
	resumed: boolean;
}

export interface FreshdeskDisconnectResult {
	disconnected: true;
	/** Rule keys machine successfully deleted from Freshdesk. */
	removed_automation_rules: string[];
	/**
	 * Machine COMPUTES this — false on the common path where every recorded
	 * rule was deleted. Pinning it to the literal `true` (as this type once
	 * did) would let `if (result.manual_cleanup_required)` narrow to
	 * always-true and silently drop the else branch.
	 */
	manual_cleanup_required: boolean;
	cleanup_guidance: string;
}

interface FreshdeskHttpClient {
	post<T = unknown>(url: string, body?: unknown): Promise<{ data: T }>;
}

export const createFreshdeskIntegrationService = (client: FreshdeskHttpClient) => ({
	async verifyFreshdeskIntegration(subdomain: string, apiKey: string): Promise<FreshdeskVerificationResult> {
		const { data } = await client.post<FreshdeskVerificationResult>('/machine' + API_ENDPOINTS.FRESHDESK.VERIFY, {
			subdomain,
			api_key: apiKey
		});
		return data;
	},

	async connectFreshdeskIntegration(payload: FreshdeskConnectPayload): Promise<void> {
		await client.post('/machine' + API_ENDPOINTS.FRESHDESK.CONNECT, payload);
	},

	async replaceFreshdeskApiKey(apiKey: string): Promise<void> {
		await client.post('/machine' + API_ENDPOINTS.FRESHDESK.REPLACE_API_KEY, { api_key: apiKey });
	},

	async stageFreshdeskWebhookToken(): Promise<FreshdeskRotationResult> {
		const { data } = await client.post<FreshdeskRotationResult>('/machine' + API_ENDPOINTS.FRESHDESK.STAGE_WEBHOOK_TOKEN);
		return data;
	},

	async activateFreshdeskWebhookToken(): Promise<void> {
		await client.post('/machine' + API_ENDPOINTS.FRESHDESK.ACTIVATE_WEBHOOK_TOKEN);
	},

	async cancelFreshdeskWebhookToken(remoteRulesEdited: boolean, confirmDestructive: boolean): Promise<void> {
		await client.post('/machine' + API_ENDPOINTS.FRESHDESK.CANCEL_WEBHOOK_TOKEN, {
			remote_rules_edited: remoteRulesEdited,
			confirm_destructive: confirmDestructive
		});
	},

	async disconnectFreshdeskIntegration(): Promise<FreshdeskDisconnectResult> {
		const { data } = await client.post<FreshdeskDisconnectResult>('/machine' + API_ENDPOINTS.FRESHDESK.DISCONNECT);
		return data;
	}
});
